import { useCallback, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import debounce from "lodash.debounce";
import { gaEvent } from "@/lib/ga";
import {
  validateEmail,
  validatePassword,
  validateUsername,
} from "@/util/validate";
import { setLoadingBar } from "@/redux/siteData";
import { api } from "@/services/api";
import { errorCodes } from "@chesshotel/constants";
import { useRegister } from "@/redux/register";
import { setUserData, setLoading } from "@/redux/signIn";
import { useSize } from "@/hooks/useSize";

export type RegisterStep = "form" | "username" | "socialUsername" | "joined";

export interface UseRegisterFormOptions {
  onStepChange?: (step: RegisterStep) => void;
  onExistingUserSignIn?: () => void;
}

export function useRegisterForm(options: UseRegisterFormOptions = {}) {
  const { onStepChange, onExistingUserSignIn } = options;
  const router = useRouter();
  const { guestElo, progressKey, guestUsername } = useRegister();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [news, setNews] = useState(0);
  const [step, setStep] = useState<RegisterStep>("form");
  const [signedUpWithEmail, setSignedUpWithEmail] = useState(false);
  const [socialToken, setSocialToken] = useState<string | null>(null);
  const [socialAccountType, setSocialAccountType] = useState<
    "facebook" | "apple" | "google" | null
  >(null);

  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Track if we've already processed the redirect from sign-in
  const hasProcessedRedirect = useRef(false);

  // Notify parent of step changes
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Initialize
  useEffect(() => {
    if (guestUsername) {
      validateUsername(guestUsername, router.locale);
    }
    gaEvent({ category: "RegisterWithEmail", action: "StartCreateAccount" });
  }, []);

  // Handle redirect from sign-in page when user doesn't exist
  useEffect(() => {
    // Only process once and if we're still on the form step
    if (hasProcessedRedirect.current || step !== "form") return;

    const { accountType, credential } = router.query;
    if (
      accountType === "google" &&
      credential &&
      typeof credential === "string"
    ) {
      // User was redirected from sign-in because they don't have an account
      // We already know the user doesn't exist (got 204 from backend)
      // So we can directly proceed to username step without calling backend again
      hasProcessedRedirect.current = true;
      setSocialToken(credential);
      setSocialAccountType("google");
      setStep("socialUsername");

      // Clean up the query params from URL
      router.replace("/register", undefined, { shallow: true });
    }
  }, [router.query, step]);

  // Validation functions
  const debouncedValidateEmail = useCallback(
    debounce(async (val: string) => {
      setEmailError(await validateEmail(val));
    }, 700),
    []
  );

  const debouncedValidateUsername = useCallback(
    debounce(async (val: string) => {
      setUsernameError(await validateUsername(val, router.locale));
    }, 700),
    [router.locale]
  );

  const debouncedValidatePassword = useCallback(
    debounce(async (val: string) => {
      setPasswordError(validatePassword(val));
    }, 700),
    []
  );

  // Form change handlers
  async function emailChange(val: string) {
    setEmail(val);
    debouncedValidateEmail(val);
  }

  async function usernameChange(val: string) {
    setUsername(val);
    debouncedValidateUsername(val);
  }

  function passwordChange(val: string) {
    setPassword(val);
    debouncedValidatePassword(val);
  }

  // Account creation
  async function validateCreateAccount() {
    const passwordErr = validatePassword(password);
    const emailErr = await validateEmail(email);
    const usernameErr = await validateUsername(username, router.locale);

    setPasswordError(passwordErr);
    setEmailError(emailErr);
    setUsernameError(usernameErr);

    if (!passwordErr && !emailErr && !usernameErr) {
      createAccountRequest();
    }
  }

  function createAccountRequest() {
    setLoadingBar(true);

    const lang = router.locale;
    const elo = guestElo;
    const buildTarget = "web";

    api
      .post(
        "/users",
        {
          username,
          email,
          password,
          lang,
          country: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
          elo,
          news,
          buildTarget,
          progressKey,
        },
        {
          validateStatus: function (status) {
            return status < 400;
          },
        }
      )
      .then((response) => {
        if (response.data) {
          setSignedUpWithEmail(true);
          setStep("joined");
          gaEvent({ category: "RegisterWithEmail", action: "CreateAccount" });
          setUserData(response.data);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .catch((error) => {
        if (error?.response?.data === errorCodes.ERROR_USERNAME_NOT_AVAILABLE) {
          setUsernameError(errorCodes.ERROR_USERNAME_NOT_AVAILABLE);
        } else if (
          error?.response?.data === errorCodes.ERROR_EMAIL_NOT_AVAILABLE
        ) {
          setEmailError(errorCodes.ERROR_EMAIL_NOT_AVAILABLE);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .then(() => {
        setLoadingBar(false);
      });
  }

  async function advanceToUsernameStep() {
    const passwordErr = validatePassword(password);
    const emailErr = await validateEmail(email);
    setPasswordError(passwordErr);
    setEmailError(emailErr);
    if (!passwordErr && !emailErr) {
      setStep("username");
    }
  }

  // Social sign-in handlers
  function inlineSignInOrWillCreateFB(accessToken: string) {
    api
      .post("/auth/facebook/token", { access_token: accessToken })
      .then((response) => {
        if (!response?.data) return;
        if (response.data.willCreateFacebookUser) {
          setSocialToken(accessToken);
          setSocialAccountType("facebook");
          setStep("socialUsername");
        } else {
          setUserData(response.data);
          setLoading(false);
          if (onExistingUserSignIn) {
            onExistingUserSignIn();
          } else {
            setStep("joined");
          }
        }
      })
      .catch(() => {
        setLoading(false);
      })
      .then(() => {
        setLoadingBar(false);
      });
  }

  function signInFBInline() {
    setLoadingBar(true);
    if (window.FB) {
      window.FB.login(
        (response) => {
          if (response.status === "connected") {
            const accessToken = response.authResponse.accessToken;
            inlineSignInOrWillCreateFB(accessToken);
          } else {
            setLoadingBar(false);
          }
        },
        { scope: "gaming_profile" }
      );
    } else {
      setTimeout(() => {
        signInFBInline();
      }, 100);
    }
  }

  function inlineSignInOrWillCreateAppleUser(token: string) {
    api
      .post("/auth/apple/token", { token })
      .then((response) => {
        if (!response?.data) return;
        if (response.data.willCreateAppleUser) {
          setSocialToken(token);
          setSocialAccountType("apple");
          setStep("socialUsername");
        } else {
          setUserData(response.data);
          setLoading(false);
          if (onExistingUserSignIn) {
            onExistingUserSignIn();
          } else {
            setStep("joined");
          }
        }
      })
      .catch(() => {
        setLoading(false);
      })
      .then(() => {
        setLoadingBar(false);
      });
  }

  async function signInAppleWebInline() {
    setLoadingBar(true);
    try {
      const data = await window.AppleID.auth.signIn();
      if (data.authorization) {
        inlineSignInOrWillCreateAppleUser(data.authorization.id_token);
      } else {
        setLoadingBar(false);
      }
    } catch (error) {
      setLoadingBar(false);
    }
  }

  function inlineSignInOrWillCreateGoogleUser(credential: string) {
    setLoadingBar(true);
    api
      .post("/auth/google/token", { credential })
      .then((response) => {
        if (response.status === 200 && response.data) {
          // User exists, sign them in
          setUserData(response.data);
          setLoading(false);
          if (onExistingUserSignIn) {
            onExistingUserSignIn();
          } else {
            setStep("joined");
          }
        } else if (response.status === 204) {
          // User doesn't exist, proceed to username step
          setSocialToken(credential);
          setSocialAccountType("google");
          setStep("socialUsername");
        }
      })
      .catch((error) => {
        setLoading(false);
        // Handle different error scenarios
        if (error?.response?.status === 400) {
          // Invalid token - this shouldn't happen with valid Google credentials
          // But if it does, we'll still allow them to proceed to registration
          // as the token might be valid but there's a temporary issue
          console.error(
            "Google token validation failed:",
            error.response?.data
          );
          // Proceed to registration step - the backend will validate again when creating account
          setSocialToken(credential);
          setSocialAccountType("google");
          setStep("socialUsername");
        } else {
          // Other errors (network, etc.) - don't proceed
          console.error("Google sign-in error:", error);
        }
      })
      .then(() => {
        setLoadingBar(false);
      });
  }

  function initializeGoogleButton() {
    // Only initialize when on form step
    if (step !== "form") return;

    const g = (window as any).google;
    if (!g?.accounts?.id) return;

    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    g.accounts.id.initialize({
      client_id: clientId,
      callback: (res: any) => {
        console.log("✅ Google credential received:", res);
        inlineSignInOrWillCreateGoogleUser(res.credential);
      },
      // Use FedCM to avoid popup/opener issues on COOP/COEP pages
      // FedCM does not rely on window.opener postMessage
      use_fedcm_for_prompt: true,
    });

    const buttonWidth = Math.min(400, window.innerWidth - 42);

    const gbtn = document.getElementById("gbtn");
    if (gbtn) {
      // Clear any existing button content before rendering
      gbtn.innerHTML = "";
      g.accounts.id.renderButton(gbtn, {
        theme: "outline",
        longtitle: true,
        size: "large",
        width: buttonWidth,
        text: "continue_with",
        logo_alignment: "left",
      });
    }
  }

  useEffect(() => {
    // Only initialize Google button when we're on the form step
    if (step !== "form") return;

    // Initialize Google button when component mounts or when navigating to this page
    // Check if Google script is already loaded
    if ((window as any).google?.accounts?.id) {
      initializeGoogleButton();
    } else {
      // If not loaded yet, wait a bit and try again
      const checkInterval = setInterval(() => {
        if ((window as any).google?.accounts?.id) {
          clearInterval(checkInterval);
          initializeGoogleButton();
        }
      }, 100);

      // Cleanup after 5 seconds if script doesn't load
      setTimeout(() => clearInterval(checkInterval), 5000);
    }
  }, [step, router.locale]);

  async function createSocialAccount() {
    // Validate then create social user
    const err = await validateUsername(username, router.locale);
    setUsernameError(err);
    if (err) return;
    if (!socialToken || !socialAccountType) return;
    setLoadingBar(true);
    let request;
    if (socialAccountType === "facebook") {
      request = api.post("/users/facebook", {
        username,
        token: socialToken,
        progressKey,
        lang: router.locale,
      });
    } else if (socialAccountType === "apple") {
      request = api.post("/users/apple", {
        username,
        token: socialToken,
        progressKey,
        lang: router.locale,
      });
    } else {
      request = api.post("/auth/google/token", {
        credential: socialToken,
        username,
        progressKey,
        lang: router.locale,
        elo: guestElo,
      });
    }
    request
      .then((res) => {
        if (res?.data) {
          setSignedUpWithEmail(false);
          setUserData(res.data);
          setLoading(false);
          setStep("joined");
        }
      })
      .catch((error) => {
        if (error?.response?.data === errorCodes.ERROR_USERNAME_NOT_AVAILABLE) {
          setUsernameError(errorCodes.ERROR_USERNAME_NOT_AVAILABLE);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .then(() => setLoadingBar(false));
  }

  const failStyle = { border: "1px solid #ff4900" } as const;
  const usernameStyle = usernameError && failStyle;
  const emailStyle = emailError && failStyle;
  const passwordStyle = passwordError && failStyle;

  return {
    // State
    username,
    email,
    password,
    news,
    step,
    signedUpWithEmail,
    usernameError,
    emailError,
    passwordError,
    usernameStyle,
    emailStyle,
    passwordStyle,
    guestElo,
    // Handlers
    setNews,
    emailChange,
    usernameChange,
    passwordChange,
    advanceToUsernameStep,
    validateCreateAccount,
    createSocialAccount,
    signInFBInline,
    signInAppleWebInline,
    initializeGoogleButton,
  };
}
